import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export function useDocumentUpload(taxReturnId: string) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { toast } = useToast();

  const uploadDocument = async (
    file: File,
    documentType: string,
    userId: string,
    financialYear: string
  ) => {
    if (!taxReturnId || !userId) {
      toast({
        title: "Error",
        description: "Missing required information for upload",
        variant: "destructive",
      });
      return null;
    }

    setIsUploading(true);
    setUploadProgress(0);
    let uploadedFilePath = ''; // Define the variable here
    
    try {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size exceeds 10MB limit');
      }

      // Create organized folder structure: userId/financialYear/taxReturnId/filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      uploadedFilePath = `${userId}/${financialYear}/${taxReturnId}/${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('tax_documents')
        .upload(uploadedFilePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Update progress after successful upload
      setUploadProgress(100);

      // Store file metadata in the database
      const { error: dbError } = await supabase
        .from('tax_documents')
        .insert({
          user_id: userId,
          tax_return_id: taxReturnId,
          file_name: file.name,
          file_path: uploadedFilePath,
          file_type: file.type,
          file_size: file.size,
          document_type: documentType,
        });

      if (dbError) throw dbError;

      toast({
        title: "Success",
        description: "Document uploaded successfully",
      });

      return {
        name: file.name,
        size: file.size,
        type: file.type,
        path: uploadedFilePath,
      };
    } catch (error: any) {
      console.error('Upload error:', error);
      
      // Attempt to clean up the uploaded file if database insertion fails
      if (uploadedFilePath && error?.message?.includes('tax_documents')) {
        await supabase.storage
          .from('tax_documents')
          .remove([uploadedFilePath]);
      }

      toast({
        title: "Error",
        description: error.message || "Failed to upload document",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return {
    isUploading,
    uploadProgress,
    uploadDocument,
  };
}